import { V2 } from '../../../theme/tokens'
/**
 * ActivityTimeline — shared event-type palette.
 *
 * Central registry of icon + color coding for common activity event types.
 * Consumers (RMA, PO, etc.) map their domain-specific event type strings to
 * one of these canonical types so the visual vocabulary is consistent.
 *
 * How to extend: add a new key here + MUI icon + palette, and export the
 * string constant through EVENT_TYPES. Both RMA and PO will pick it up on
 * next render. Do NOT fork this map per domain — that defeats the point.
 */
import ArrowForwardIcon from '@mui/icons-material/ArrowForward'
import SendIcon from '@mui/icons-material/Send'
import EmailIcon from '@mui/icons-material/Email'
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead'
import StickyNote2Icon from '@mui/icons-material/StickyNote2'
import EditIcon from '@mui/icons-material/Edit'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import PaymentsIcon from '@mui/icons-material/Payments'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import LinkIcon from '@mui/icons-material/Link'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'
import CurrencyExchangeIcon from '@mui/icons-material/CurrencyExchange'
import ScheduleIcon from '@mui/icons-material/Schedule'
import SettingsIcon from '@mui/icons-material/Settings'
import WarningIcon from '@mui/icons-material/Warning'

/** Canonical event type keys. Consumers map their entries to one of these. */
export const EVENT_TYPES = {
  STATUS_CHANGE:  'status_change',
  EMAIL_SENT:     'email_sent',
  EMAIL_RECEIVED: 'email_received',
  NOTE:           'note',
  CHANGE:         'change',
  ERROR:          'error',
  WARNING:        'warning',
  PAYMENT:        'payment',
  INVOICE:        'invoice',
  SHIPPING:       'shipping',
  FX:             'fx',
  LINK:           'link',
  SYSTEM:         'system',
  // Internal marker — pseudo-entry inserted by the duration-gap feature.
  DURATION_GAP:   '_duration_gap',
}

/**
 * Visual palette per event type. Each entry: { Icon, bg, border }.
 * Icons are MUI icon components (not instantiated — the timeline instantiates
 * them with the right size). Bg / border are CSS color strings used by
 * TimelineIcon.
 */
export const DEFAULT_PALETTE = {
  status_change:  { Icon: ArrowForwardIcon,    bg: V2.okSoft, border: V2.okText },
  email_sent:     { Icon: SendIcon,            bg: V2.infoSoft, border: V2.infoText },
  email_received: { Icon: MarkEmailReadIcon,   bg: V2.primary100, border: V2.infoText },
  note:           { Icon: StickyNote2Icon,     bg: V2.warnSoft, border: V2.warnText },
  change:         { Icon: EditIcon,            bg: '#e5e7eb', border: V2.ink70 },
  error:          { Icon: ErrorOutlineIcon,    bg: V2.errSoft, border: V2.errText },
  warning:        { Icon: WarningIcon,         bg: V2.warnSoft, border: V2.warnText },
  payment:        { Icon: PaymentsIcon,        bg: V2.okSoft, border: V2.okText },
  invoice:        { Icon: ReceiptLongIcon,     bg: '#ede9fe', border: V2.agent },
  shipping:       { Icon: LocalShippingIcon,   bg: V2.infoSoft, border: V2.infoText },
  fx:             { Icon: CurrencyExchangeIcon,bg: V2.warnSoft, border: V2.warnText },
  link:           { Icon: LinkIcon,            bg: V2.primary100, border: V2.infoText },
  system:         { Icon: SettingsIcon,        bg: V2.wash, border: V2.ink45 },
  // Duration gap has no icon; timeline renders it as a divider.
  _duration_gap:  { Icon: ScheduleIcon,        bg: V2.hoverTint, border: '#d1d5db' },
  // Generic fallback when entry.type doesn't match a known key.
  _default:       { Icon: EmailIcon,           bg: V2.wash, border: V2.ink70 },
}

/**
 * Row-tint palette by severity. Used by ActivityTimeline when the consumer
 * passes `severityFor(entry)`. Applied as a subtle background + left-border
 * on the TimelineContent block.
 */
export const SEVERITY_TINT = {
  error:   { bg: 'rgba(220, 38, 38, 0.06)',  border: V2.errText },
  warning: { bg: 'rgba(234,  88, 12, 0.06)', border: V2.warnText },
  info:    { bg: 'rgba(37,  99, 235, 0.04)', border: V2.infoText },
  success: { bg: 'rgba( 5, 150, 105, 0.05)', border: V2.okText },
}

/**
 * Resolve an entry to its palette. Order:
 *   1. Consumer-supplied iconFor(entry) result (if non-null)
 *   2. DEFAULT_PALETTE[entry.type]
 *   3. DEFAULT_PALETTE._default
 * Returns { Icon, bg, border }.
 */
export function resolvePalette(entry) {
  return (entry && DEFAULT_PALETTE[entry.type]) || DEFAULT_PALETTE._default
}
